import React from 'react';
import { FormFieldLabel, Table } from '@vwfs-bronson/bronson-react';
import './style.css';
import { useAppSelector } from "../../../redux/hooks";
import { selectCustomer } from "../../../redux/slices/customer-slice";
import {
  PaymentProfileCreditCardData,
  PaymentProfileHistory,
  PaymentProfilePayPalData,
  PaymentProfileSepaDirectDebitData
} from "../../../models/customer-data.interface";
import DetailComponent from "../../base/detail-component";
import { formatter } from '../../../utils/time-utils';
import InfoIconExtended from '../../base/info-icon-extended';
import TooltipInfoIcon from '../../base/tooltip-info-icon';


export default function PaymentMethodDataView() {
  const customer = useAppSelector(selectCustomer());
  let paymentProfiles = customer?.customer?.paymentProfiles && customer?.customer?.paymentProfiles?.length > 0 ? [...customer.customer.paymentProfiles]: [];
  /* sorting by
    1. provider
    2. priority
    3. expiry
   */
  paymentProfiles = paymentProfiles.sort((a,b) => {
    return a?.provider === b?.provider ?
      (a?.priority - b?.priority ) : ( a?.provider > b?.provider ? 1 : -1)
  });

  function renderItem(pm: (PaymentProfileCreditCardData | PaymentProfileSepaDirectDebitData | PaymentProfilePayPalData)) {
    return (
      <Table.Tr key={pm.paymentProfileId} className={`table-row ${pm.priority === 1 && 'default-pm'}`}  >
        <Table.Td>{pm.paymentProfileId.substring(0, 9) + '...'} <InfoIconExtended label={pm.paymentProfileId} /></Table.Td>
        <Table.Td>{pm.provider ? pm.provider : 'SMARTPAY'}</Table.Td>
        <Table.Td>{pm.paymentMethod}</Table.Td>
        <Table.Td>{pm.state}</Table.Td>
        <Table.Td>{pm.priority}</Table.Td>
        {renderItemDetails(pm)}
      </Table.Tr>
    );
  }

  function renderHistoryItem(pm: (PaymentProfileHistory)) {
    return (
      <Table.Tr key={pm.id} className='table-row'>
        <Table.Td>{pm.provider ? pm.provider : 'SMARTPAY'}</Table.Td>
        <Table.Td>{pm.createdAt ? formatter.format(new Date(pm.createdAt)) : '-'}</Table.Td>
        <Table.Td>{pm.updatedAt ? formatter.format(new Date(pm.updatedAt)) : '-'}</Table.Td>
        <Table.Td>{pm.expiresAt || '-'}</Table.Td>
        <Table.Td>{renderState(pm)}</Table.Td>
      </Table.Tr>
    );
  }

  function renderState(pm: (PaymentProfileHistory)) {
    return (
      <>
        {pm.state}
        {pm.error && (
          <TooltipInfoIcon>
            {pm.error?.details}<br />
            {pm.error?.code}<br />
            {decodeURI(pm.error?.message)}<br />
          </TooltipInfoIcon>
        )}
      </>
    );
  }

  function renderItemDetails(pm: (PaymentProfileCreditCardData | PaymentProfileSepaDirectDebitData | PaymentProfilePayPalData)) {
    switch (pm.paymentMethod) {
      case 'CREDIT_CARD': {
        const pmdetails = pm as PaymentProfileCreditCardData;
        return (<Table.Td>{pmdetails.creditCard?.cardNetwork}(*** {pmdetails.creditCard?.last4}), expiry: {pmdetails.creditCard?.expiryMonth}/{pmdetails.creditCard?.expiryYear}</Table.Td>)
      }
      case 'SEPA_DIRECT_DEBIT': {
        const pmdetails = pm as PaymentProfileSepaDirectDebitData;
        return (<Table.Td>{pmdetails.sepaDirectDebit?.bankName}</Table.Td>)
      }
      case 'PAYPAL': {
        const pmdetails = pm as PaymentProfilePayPalData;
        return (<Table.Td>{pmdetails.paypal?.email}</Table.Td>)
      }
      default:
        return (<Table.Td>---</Table.Td>)
    }
  }

  return (
    <>
      <div>
        <div className='payment-details'>
          <DetailComponent id='paymentMethodStatus' label='Payment Method Status' value={customer.customer?.paymentMethodStatus} />
        </div>
      </div>
      <div className="customer-detail" data-testid="tableComponent">
        <p>Active Payment Methods</p>
        {paymentProfiles && paymentProfiles?.length > 0 && (
          <Table colored={true} testId={'payment-profiles-table'}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>ID</Table.Th>
                <Table.Th>Provider</Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>State</Table.Th>
                <Table.Th>Priority</Table.Th>
                <Table.Th>Details</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{paymentProfiles?.map((pm) => renderItem(pm))}</Table.Tbody>
          </Table>
        )}

        {(!paymentProfiles || paymentProfiles?.length === 0) && (<FormFieldLabel testId="noPaymantProfilesLabel">No Payment Methods</FormFieldLabel>)}
      </div>

      <div className="customer-detail" data-testid="pmHistoryTableComponent">
        <p>Payment Methods History</p>
        {customer.customer?.paymentProfilesHistory && customer.customer?.paymentProfilesHistory?.length > 0 && (
          <Table colored={true} testId={'payment-profiles-history-table'}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Provider</Table.Th>
                <Table.Th>Created At</Table.Th>
                <Table.Th>Updated At</Table.Th>
                <Table.Th>Expires At</Table.Th>
                <Table.Th>State</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{customer.customer?.paymentProfilesHistory?.map((pm) => renderHistoryItem(pm))}</Table.Tbody>
          </Table>
        )}

        {(!customer.customer?.paymentProfilesHistory || customer.customer?.paymentProfilesHistory?.length === 0) && (<FormFieldLabel testId="noPaymantProfilesLabel">No Payment Profiles History Found</FormFieldLabel>)}
      </div>
    </>
  );
}
